/**
 * LayerStatesDialog — the Layer State Manager (Faz 5.4, OCS layer_state_manager
 * parity): a left list of named snapshots (searchable, "N layers" caption)
 * with New / Restore / Delete actions on the right, plus the per-property
 * restore mask (On/Freeze/Lock/Color/Linetype/Lineweight/Transparency).
 * Saving captures the full current layer table through the shared
 * useLayerStateStore (same store the LAYERSTATE command writes).
 */

import {
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../state/store';
import {
    LAYER_STATE_MASKS,
    useLayerStateStore,
    type LayerStateMask,
} from '../../state/commandAuxStores';

export interface LayerStatesDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export function LayerStatesDialog({ isOpen, onClose }: LayerStatesDialogProps) {
    const { t } = useTranslation();
    const states = useLayerStateStore((s) => s.states);
    const masks = useLayerStateStore((s) => s.masks);
    const layers = useAppStore((s) => s.layers);
    const activeLayerId = useAppStore((s) => s.activeLayerId);

    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string | null>(null);
    const [newName, setNewName] = useState('');
    const [maskDraft, setMaskDraft] = useState<LayerStateMask[]>([...LAYER_STATE_MASKS]);

    const names = useMemo(() => {
        const q = search.trim().toLowerCase();
        return Object.keys(states)
            .filter((n) => !q || n.toLowerCase().includes(q))
            .sort((a, b) => a.localeCompare(b));
    }, [states, search]);

    const pick = (name: string) => {
        setSelected(name);
        setMaskDraft(masks[name] ? [...masks[name]] : [...LAYER_STATE_MASKS]);
    };

    const saveNew = () => {
        const name = newName.trim();
        if (!name) return;
        const app = useAppStore.getState();
        useLayerStateStore.getState().save(
            name,
            Object.values(app.layers).map((l) => ({
                id: l.id, visible: l.visible, frozen: !!l.frozen, locked: l.locked,
                current: app.activeLayerId === l.id, color: l.color,
                linetype: l.linetype, lineweight: l.lineweight,
                transparency: l.transparency, plottable: l.plottable !== false,
            })),
            [...maskDraft],
        );
        setNewName('');
        setSelected(name);
    };

    const restore = (name: string) => {
        const records = states[name];
        if (!records) return;
        const mask = new Set(masks[name] ?? LAYER_STATE_MASKS);
        const app = useAppStore.getState();
        records.forEach((rec) => {
            const layer = Object.values(app.layers).find((l) => l.id === rec.id)
                ?? Object.values(app.layers).find((l) => l.name === rec.id);
            if (!layer) return;
            const updates: Record<string, unknown> = {};
            if (mask.has('ON')) updates.visible = rec.visible;
            if (mask.has('FROZEN')) updates.frozen = rec.frozen;
            if (mask.has('LOCKED')) updates.locked = rec.locked;
            if (mask.has('COLOR') && rec.color) updates.color = rec.color;
            if (mask.has('LINE_TYPE') && rec.linetype !== undefined) updates.linetype = rec.linetype;
            if (mask.has('LINE_WEIGHT') && rec.lineweight !== undefined) updates.lineweight = rec.lineweight;
            if (mask.has('TRANSPARENCY') && rec.transparency !== undefined) updates.transparency = rec.transparency;
            app.updateLayer(layer.id, updates);
            if (rec.current) app.setActiveLayer(layer.id);
        });
        onClose();
    };

    const remove = (name: string) => {
        useLayerStateStore.getState().remove(name);
        if (selected === name) setSelected(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent bg="bg.panel">
                <ModalHeader fontSize="sm">{t('ui.cad.layerStates.title')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Flex gap={3} minH="280px">
                        {/* Left: named states list */}
                        <VStack spacing={2} align="stretch" flex="1" minW={0}>
                            <Input
                                size="sm"
                                variant="filled"
                                bg="surface.300"
                                color="text.primary"
                                placeholder={t('ui.cad.layerStates.search')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <Box flex="1" overflowY="auto" border="1px solid" borderColor="border.base" borderRadius="4px">
                                {names.length === 0 && (
                                    <Text px={3} py={2} fontSize="xs" color="text.secondary">
                                        {t('ui.cad.layerStates.empty')}
                                    </Text>
                                )}
                                {names.map((name) => (
                                    <Flex
                                        key={name}
                                        as="button"
                                        px={3}
                                        py={1.5}
                                        w="full"
                                        align="center"
                                        justify="space-between"
                                        bg={selected === name ? 'bg.active' : 'transparent'}
                                        _hover={{ bg: 'bg.hover' }}
                                        onClick={() => pick(name)}
                                        flexShrink={0}
                                    >
                                        <Text fontSize="sm" color="text.primary" noOfLines={1}>{name}</Text>
                                        <Text fontSize="10px" color="text.secondary" flexShrink={0}>
                                            {t('ui.cad.layerStates.layerCount', { n: states[name]?.length ?? 0 })}
                                        </Text>
                                    </Flex>
                                ))}
                            </Box>
                            <Text fontSize="10px" color="text.secondary">
                                {t('ui.cad.layerStates.layerCount', { n: names.length })}
                            </Text>
                        </VStack>

                        {/* Right: actions + restore mask */}
                        <VStack spacing={3} align="stretch" w="200px" flexShrink={0}>
                            <Input
                                size="sm"
                                variant="filled"
                                bg="surface.300"
                                color="text.primary"
                                placeholder={t('ui.cad.layerStates.newName')}
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveNew(); }}
                            />
                            <Button size="sm" onClick={saveNew} isDisabled={!newName.trim()}>
                                {t('ui.cad.layerStates.saveNew')}
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="blue"
                                isDisabled={!selected || !states[selected!]}
                                onClick={() => restore(selected!)}
                            >
                                {t('ui.cad.layerStates.restore')}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                color="text.secondary"
                                isDisabled={!selected}
                                onClick={() => remove(selected!)}
                            >
                                {t('ui.cad.layerStates.delete')}
                            </Button>

                            <Box>
                                <Text fontSize="10px" color="text.secondary" textTransform="uppercase" mb={1}>
                                    {t('ui.cad.layerStates.mask')}
                                </Text>
                                <CheckboxGroup
                                    value={maskDraft}
                                    onChange={(vals) => setMaskDraft(vals as LayerStateMask[])}
                                >
                                    <Stack spacing={1}>
                                        {LAYER_STATE_MASKS.map((m) => (
                                            <Checkbox key={m} size="sm" value={m} colorScheme="blue">
                                                <Text fontSize="xs" color="text.primary">
                                                    {t(`ui.cad.layerStates.mask_${m}`)}
                                                </Text>
                                            </Checkbox>
                                        ))}
                                    </Stack>
                                </CheckboxGroup>
                            </Box>
                        </VStack>
                    </Flex>
                </ModalBody>
                <ModalFooter>
                    <Button size="sm" variant="ghost" onClick={onClose}>
                        {t('ui.cad.layerStates.close')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
